// Installed → "Button rows": the devices whose buttons have no row to rename,
// and the repair.
//
// ⚠️ WHY THIS SCREEN EXISTS (2026-10-23, a customer complaint — Satish Ahuja,
// Vani room, "window side"): a rename writes `UPDATE OutputDetails … WHERE
// UniqueNumber = ? AND Output = ?`, and a device with no row matches nothing
// while the server still answers HTTP 200. 49 of 464 live devices had no output
// rows at all, 9 more were missing some. The app now creates the row when a
// rename finds none (core/devices/outputRows), and this repairs them in bulk
// before a customer ever meets it.
//
// ⚠️ IT ONLY INSERTS, AND IT SHOWS THE COUNT FIRST — the two rules the old
// backend cron broke (it deleted every row of a device whose count mismatched,
// customer names and all).
//
// ⚠️ A MISSING ROW IS NOT ALWAYS A FAULT, so the types that have always run
// without rows (fan 53, WWH 25/90, AC touch 60, BLE 100) are listed UNTICKED
// with the reason, and nothing is written unless somebody ticks them.
// core/devices/outputRows.SKIP_BY_DEFAULT is that list.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, LinearProgress, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useApp } from '../../core/state/AppContext';
import { loadOutputRowRepair, runOutputRowRepair } from '../../core/admin/outputRowRepair';
import { repairSummary, SKIP_BY_DEFAULT } from '../../core/devices/outputRows';
import { showToast } from './toast';

const errText = (e) => e?.response?.data?.error || e?.message || 'Could not read the list.';

export default function ButtonRowsPanel() {
  const { isSuperAdmin } = useApp();
  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    setResult(null);
    setProgress({ done: 0, total: 0, label: 'Asking the server…' });
    try {
      const rows = await loadOutputRowRepair({
        onProgress: (done, total) => setProgress({ done, total, label: 'Reading each device’s rows…' }),
      });
      setPlan(rows);
    } catch (e) {
      setError(errText(e));
      setPlan([]);
    } finally {
      setProgress(null);
    }
  }, []);

  useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin, load]);

  if (!isSuperAdmin) {
    return <Box sx={{ p: 3 }}><Alert severity="error">Super Admin only.</Alert></Box>;
  }

  const tick = (uno, on) => setPlan((prev) => (prev || []).map((d) => (
    String(d.UniqueNumber) === String(uno) ? { ...d, ticked: on } : d)));

  const summary = repairSummary(plan || []);

  const repair = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await runOutputRowRepair(plan, {
        onProgress: (done, total) => setProgress({ done, total, label: 'Creating the rows…' }),
      });
      setResult(res);
      showToast(
        res.failed.length
          ? `${res.created} row(s) created, ${res.failed.length} failed`
          : `${res.created} button row(s) created on ${res.devices} device(s)`,
        { severity: res.failed.length ? 'warning' : 'success' },
      );
      await load();
    } catch (e) {
      setError(errText(e));
    } finally {
      setProgress(null);
      setBusy(false);
    }
  };

  const pct = progress && progress.total ? Math.round((progress.done / progress.total) * 100) : null;

  return (
    <Box sx={{ p: 1, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Button
          size="small" startIcon={<RefreshIcon />} onClick={load} disabled={busy || !!progress}
        >
          Re-read
        </Button>
        <Button
          size="small" variant="contained" startIcon={<BuildIcon />}
          disabled={busy || !!progress || !summary.devices}
          onClick={repair}
        >
          {`Create missing rows — ${summary.text}`}
        </Button>
        <Box sx={{ flex: 1 }} />
        {plan && (
          <Typography variant="caption" color="text.secondary">
            {`${plan.length} device(s) with missing button rows`}
          </Typography>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        A button whose <code>OutputDetails</code> row is missing cannot be renamed — the save
        reaches no row and the server still answers OK. Creating the row gives it the backend’s
        own default name (<code>O_&lt;number&gt;_&lt;n&gt;</code>); the customer renames it after.
        Nothing here is ever deleted.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {progress && (
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 12, mb: 0.5 }}>{progress.label}</Typography>
          <LinearProgress variant={pct == null ? 'indeterminate' : 'determinate'} value={pct == null ? undefined : pct} />
        </Box>
      )}

      {result && (
        <Alert severity={result.failed.length ? 'warning' : 'success'} sx={{ mb: 1 }}>
          {`${result.created} row(s) created on ${result.devices} device(s).`}
          {result.failed.map((f) => (
            <div key={`${f.uno}-${f.output}`}>{`${f.uno} output ${f.output}: ${f.error}`}</div>
          ))}
        </Alert>
      )}

      {plan && !plan.length && !progress && (
        <Alert severity="success">Every device has all of its button rows.</Alert>
      )}

      {!!(plan && plan.length) && (
        <Paper variant="outlined" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Device</TableCell>
                <TableCell>Where</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Has</TableCell>
                <TableCell align="right">Expects</TableCell>
                <TableCell>Missing outputs</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plan.map((d) => {
                const unreadable = d.missing == null;
                const skipped = SKIP_BY_DEFAULT.includes(Number(d.DeviceID));
                return (
                  <TableRow key={String(d.UniqueNumber)} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={!!d.ticked}
                        disabled={unreadable || busy}
                        onChange={(e) => tick(d.UniqueNumber, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{d.UniqueNumber}</Typography>
                      <Typography variant="caption" color="text.secondary">{d.DeviceName || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {[d.LocationName, d.RoomName].filter(Boolean).join(' · ') || 'not placed'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{`${d.DeviceID} ${d.TypeName || ''}`}</Typography>
                      {skipped && (
                        <Chip
                          size="small" variant="outlined" sx={{ ml: 0.5 }} label="rows not used by this type"
                        />
                      )}
                      {/* ⚠️ Two install rows for one unique number (1128094 is
                          815 and 1502) — repaired once, by the number. */}
                      {!!(d.duplicateIds && d.duplicateIds.length) && (
                        <Chip
                          size="small" color="warning" variant="outlined" sx={{ ml: 0.5 }}
                          label={`duplicate install row: also ID ${d.duplicateIds.join(', ')}`}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">{d.actual}</TableCell>
                    <TableCell align="right">{d.expected}</TableCell>
                    <TableCell>
                      {unreadable
                        ? <Chip size="small" color="warning" variant="outlined" label="could not read its rows" />
                        : d.missing.join(', ')}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
